import { Injectable } from '@angular/core';
import { AssetGroups, RecentQuotesItem, SecuritySymbol } from '../../domain/public-api';

type QueuedSymbol = SecuritySymbol & { match?: boolean };
/**
 * Recent Symbols Queue Service
 */
@Injectable({
  providedIn: 'root'
})
export class RecentSymbolsQueueService {
  /**
   * Maximum Symbol Queue Size
   */
  private static readonly MAX_QUEUE_SIZE: number = 10;

  /**
   * Symbol Queue
   */
  private queue: Array<QueuedSymbol> = [];

  /**
   * Get list of recent symbols
   *
   * @returns Returns array of symbols
   */
  getSymbols(): Array<SecuritySymbol> {
    return this.queue;
  }

  /**
   * Returns whether symbol queue is empty
   *
   * @returns Returns true if symbol queue is empty. Otherwise, false.
   */
  isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Returns size of symbol queue
   *
   * @returns Returns size.
   */
  size(): number {
    return this.queue.length;
  }

  /**
   * Clears symbol queue
   */
  clear(): void {
    this.queue.splice(0, this.queue.length);
  }

  /**
   * Add a symbol or list of symbols to the symbol queue.
   *
   * @param symbols A symbol or list of symbols
   */
  add(symbols: SecuritySymbol | Array<SecuritySymbol>): void {
    if (symbols instanceof Array) {
      symbols.forEach((symbol: SecuritySymbol): void => {
        this.addSymbol(symbol);
      });
    } else {
      this.addSymbol(symbols);
    }
  }

  /**
   * Remove a quote from the queue
   *
   * @param recentQuote quote to remove
   */
  removeByRecentQuotes(recentQuote: RecentQuotesItem): void {
    let index = this.queue.length;
    let found = false;
    while (!found && index-- > 0) {
      found = this.isEqualSymbolQuote(this.queue[index], recentQuote);
      if (found) {
        this.remove(index);
      }
    }
  }

  /**
   * Filter recent quotes to only contain quotes that are in symbols queue.
   *
   * @param recentQuotesItems Recent quotes entries
   * @returns recent quotes entries.
   */
  filterRecentQuotes(recentQuotesItems: AssetGroups<RecentQuotesItem>): AssetGroups<RecentQuotesItem> {
    let recentQuotes: AssetGroups<RecentQuotesItem> = {} as AssetGroups<RecentQuotesItem>;
    if (recentQuotesItems !== undefined) {
      for (const assetGroup in recentQuotesItems) {
        if (Object.getOwnPropertyDescriptor(recentQuotesItems, assetGroup)) {
          recentQuotes[assetGroup] = this.filter(recentQuotesItems[assetGroup]);
        }
      }
    } else {
      recentQuotes = this.getDefaultEmptyQuotes();
    }
    this.synchQueueWithRecentQuotes();
    return recentQuotes;
  }

  /**
   * Returns default empty quote list
   *
   * @returns Returns default recent quotes entries
   */
  getDefaultEmptyQuotes(): AssetGroups<RecentQuotesItem> {
    return {
      ALL_SECURITY_TYPES: [],
      INDEX: [],
      MUTUALFUND: [],
      OPTION: [],
      STOCK: []
    } as AssetGroups<RecentQuotesItem>;
  }

  /**
   * Keep only the symbols that have been matched with quotes in the queue.
   */
  private synchQueueWithRecentQuotes(): void {
    let index: number = this.queue.length;
    while (index-- > 0) {
      if (this.queue[index].match === undefined) {
        this.remove(index);
      } else {
        delete this.queue[index].match;
      }
    }
  }

  /**
   * Add a symbol to the queue
   *
   * @param symbol Symbol to add
   */
  private addSymbol(symbol: SecuritySymbol): void {
    const key: SecuritySymbol = this.createKey(symbol);
    const index: number = this.queue.indexOf(key);
    if (index !== -1) {
      this.remove(index);
    }
    this.queue.push(key);
    if (this.queue.length > RecentSymbolsQueueService.MAX_QUEUE_SIZE) {
      this.queue.shift();
    }
  }

  /**
   * Creates a symbol key
   *
   * @param symbol Symbol to create key from
   */
  private createKey(symbol: QueuedSymbol): SecuritySymbol {
    let index: number = this.queue.length;
    while (index-- > 0) {
      if (this.isEqual(this.queue[index], symbol)) {
        return this.queue[index];
      }
    }
    return { symbol: symbol.symbol, market: symbol.market };
  }

  /**
   * Removes a symbol from queue by index
   *
   * @param index Index to remove from queue
   */
  private remove(index: number): void {
    this.queue.splice(index, 1);
  }

  /**
   * Filter quotes to only the ones that match symbols in queue and find all symbols
   * in queue that matches quote and mark them.
   *
   * @param recentQuotes Array of quotes
   * @returns Returns filtered quotes array.
   */
  private filter(recentQuotes: Array<RecentQuotesItem>): Array<RecentQuotesItem> {
    const newQuotes: Array<RecentQuotesItem> = [];
    let recentQuote;
    let index = this.queue.length;
    while (index-- > 0) {
      recentQuote = recentQuotes.filter((entry: RecentQuotesItem): boolean => this.isEqualSymbolQuote(this.queue[index], entry))[0];

      if (recentQuote !== undefined) {
        newQuotes.push(recentQuote);
        // Set match to true to remember, that the symbol is found in quotes. This is to compensate
        // on error condition, when symbols is added to the queue, meanwhile server site failed.
        // Use it later to remove symbols in the queue, which do not have corresponding quotes.
        this.queue[index].match = true;
      }
    }
    return newQuotes;
  }

  /**
   * Checks if two symbols are equal
   *
   * @param symbol1 Symbol 1
   * @param symbol2 Symbol 2
   * @returns Returns true if two symbols are equal. Otherwise, false.
   */
  private isEqual(symbol1: Pick<SecuritySymbol, 'symbol' | 'market'>, symbol2: Pick<SecuritySymbol, 'symbol' | 'market'>): boolean {
    return symbol1.symbol === symbol2.symbol && symbol1.market === symbol2.market;
  }

  /**
   * Checks if a symbol and quote are equal
   *
   * @param symbol Symbol
   * @param recentQuote Quote
   * @returns Returns true if symbol and quote are equal. Otherwise, false.
   */
  private isEqualSymbolQuote(symbol: Pick<SecuritySymbol, 'symbol' | 'market'>, recentQuote: RecentQuotesItem): boolean {
    return (
      symbol.symbol === recentQuote.symbol.tickerSymbol &&
      symbol.market === recentQuote.symbol.market
    );
  }
}
